/**
 * Absence Mapper
 * 
 * Transforms backend DTOs (LC_*) to UI models
 */

import type {
  LC_AbsenceItemOut,
  LC_ListAbsencesOut,
  LC_AbsenceScopeItemOut,
  LC_ListAbsencesByScopeListOut,
  AbsenceItem,
  AbsenceListResult,
  AbsenceTotals,
  AbsenceScopeItem,
  AbsenceScopeListResult,
} from './absence.types';

/**
 * Format date string from ISO to dd/mm/yyyy
 */
const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return '-';
  try {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

/**
 * Replace null or empty string with '-'
 */
const replaceEmpty = (value: string | null | undefined): string => {
  if (!value || value.trim() === '') return '-';
  return value;
};

/**
 * Map single absence item from backend to UI model
 */
export const mapAbsenceItem = (item: LC_AbsenceItemOut): AbsenceItem => ({
  id: replaceEmpty(item.id),
  tipo: replaceEmpty(item.type),
  descripcion: replaceEmpty(item.description),
  fechaDesde: formatDate(item.startDate),
  fechaHasta: formatDate(item.endDate),
  estado: replaceEmpty(item.status),
  fechaCreacion: formatDate(item.createdAt),
  creadoPor: replaceEmpty(item.createdBy),
});

/**
 * Extract totals from pagination metadata
 */
export const mapAbsenceTotals = (pagination: LC_ListAbsencesOut['pagination']): AbsenceTotals => ({
  licencias: pagination.totalMedicalLeaves,
  vacaciones: pagination.totalVacations,
  permisosEspeciales: pagination.totalSpecialPermits,
  permisosAdministrativos: pagination.totalAdministrativePermits,
  permisosFraccion: pagination.totalFractionPermits,
  faltas: pagination.totalAbsences,
});

/**
 * Map full absence list response to UI result
 */
export const mapAbsenceListResponse = (response: LC_ListAbsencesOut): AbsenceListResult => ({
  data: (response.data ?? []).map(mapAbsenceItem),
  page: response.pagination.currentPage,
  pageSize: response.pagination.pageSize,
  totalRecords: response.pagination.totalRecords,
  totalPages: response.pagination.totalPages,
  totals: mapAbsenceTotals(response.pagination),
});

/**
 * Map single absence scope item (adds employee fields to base mapping)
 */
export const mapAbsenceScopeItem = (item: LC_AbsenceScopeItemOut): AbsenceScopeItem => ({
  ...mapAbsenceItem(item),
  empleado: replaceEmpty(item.employeeName),
  rut: replaceEmpty(item.employeeTaxId),
});

/**
 * Map full absence scope list response to UI result
 */
export const mapAbsenceScopeListResponse = (
  response: LC_ListAbsencesByScopeListOut,
): AbsenceScopeListResult => ({
  data: (response.data ?? []).map(mapAbsenceScopeItem),
  page: response.pagination.currentPage,
  pageSize: response.pagination.pageSize,
  totalRecords: response.pagination.totalRecords,
  totalPages: response.pagination.totalPages,
  totals: mapAbsenceTotals(response.pagination),
});
